'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { sanitizeFormData } from '@/lib/security/sanitize';

interface FormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  category: string;
  priority: string;
  subject: string;
  message: string;
}

export default function ContactForm() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    category: '',
    priority: 'normal',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = [
    { value: 'technical', label: 'Technical Manufacturing' },
    { value: 'compliance', label: 'Compliance & Certification' },
    { value: 'project', label: 'Project Management' },
    { value: 'platform', label: 'Platform Support' },
    { value: 'quality', label: 'Quality Assurance' },
    { value: 'partnership', label: 'Partnership Inquiry' }
  ];

  const priorities = [
    { value: 'low', label: 'Low Priority', color: 'text-success' },
    { value: 'normal', label: 'Normal Priority', color: 'text-accent' },
    { value: 'high', label: 'High Priority', color: 'text-warning' },
    { value: 'urgent', label: 'Urgent', color: 'text-error' }
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Sanitize all form inputs before submission
    const { sanitized, errors: sanitizeErrors } = sanitizeFormData(formData as unknown as Record<string, string>);
    const hasErrors = Object.keys(sanitizeErrors).length > 0;
    if (hasErrors) {
      setIsSubmitting(false);
      return;
    }

    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/send-support-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify(sanitized),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to submit support request');
      }

      setIsSubmitting(false);
      setSubmitStatus('success');

      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          category: '',
          priority: 'normal',
          subject: '',
          message: ''
        });
        setSubmitStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('Support form submission error:', error);
      setIsSubmitting(false);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 4000);
    }
  };

  if (!isHydrated) {
    return (
      <section className="py-20 bg-background">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-card border border-border rounded-xl p-8">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
              <div className="space-y-4">
                <div className="h-12 bg-muted rounded"></div>
                <div className="h-12 bg-muted rounded"></div>
                <div className="h-32 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-background">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 border border-accent/20 rounded-full mb-6">
            <Icon name="PaperAirplaneIcon" size={20} className="text-accent" />
            <span className="font-cta text-sm text-accent tracking-wider">CONTACT FORM</span>
          </div>

          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Submit Your
            <span className="block text-accent mt-2">Support Request</span>
          </h2>

          <p className="text-lg text-muted-foreground">
            Our team will respond within 2 hours during business hours
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-8 space-y-6">
          {/* Personal Information */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-accent transition-colors duration-300"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-accent transition-colors duration-300"
                placeholder="john@aerospace.com"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-foreground mb-2">
                Company Name *
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-accent transition-colors duration-300"
                placeholder="Aerospace Innovations Inc."
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-accent transition-colors duration-300"
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>

          {/* Request Details */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-foreground mb-2">
                Support Category *
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-accent transition-colors duration-300"
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-foreground mb-2">
                Priority Level *
              </label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-accent transition-colors duration-300"
              >
                {priorities.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-2">
              Subject *
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-accent transition-colors duration-300"
              placeholder="Brief description of your inquiry"
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
              Detailed Message *
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={6}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-accent transition-colors duration-300 resize-none"
              placeholder="Please provide detailed information about your support request, including any relevant technical specifications, project requirements, or specific questions..."
            ></textarea>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-between pt-6 border-t border-border">
            <p className="text-sm text-technical-gray">
              * Required fields
            </p>

            <button
              type="submit"
              disabled={isSubmitting || submitStatus === 'success'}
              className="px-8 py-3 bg-mission-orange text-primary font-cta text-sm font-medium rounded-lg transition-all duration-300 hover:shadow-glow hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  <span>SUBMITTING...</span>
                </>
              ) : submitStatus === 'success' ? (
                <>
                  <Icon name="CheckCircleIcon" size={20} />
                  <span>SUBMITTED!</span>
                </>
              ) : (
                <>
                  <span>SUBMIT REQUEST</span>
                  <Icon name="PaperAirplaneIcon" size={20} />
                </>
              )}
            </button>
          </div>

          {/* Success Message */}
          {submitStatus === 'success' && (
            <div className="p-4 bg-success/10 border border-success/20 rounded-lg flex items-start space-x-3">
              <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-headline text-sm font-semibold text-success mb-1">
                  Request Submitted Successfully
                </h4>
                <p className="text-sm text-muted-foreground">
                  Our support team will review your request and respond within 2 hours. You will receive a confirmation email shortly.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {submitStatus === 'error' && (
            <div className="p-4 bg-error/10 border border-error/20 rounded-lg flex items-start space-x-3">
              <Icon name="ExclamationCircleIcon" size={24} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-headline text-sm font-semibold text-error mb-1">
                  Submission Failed
                </h4>
                <p className="text-sm text-muted-foreground">
                  Something went wrong. Please try again or contact us directly at{' '}
                  <a href="mailto:info@wingsbharath.com" className="text-accent hover:underline">info@wingsbharath.com</a>{' '}
                  or call <a href="tel:+919900008890" className="text-accent hover:underline">+91 9900008890</a>.
                </p>
              </div>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}